<template>
  <v-card variant="outlined" class="pa-6" max-width="480" data-cy="account-license-box">
    <div class="d-flex align-center mb-4">
      <div class="text-h6">Account &amp; License</div>
      <v-spacer />
      <v-btn
        v-if="!editing"
        icon="mdi-pencil-outline"
        variant="text"
        size="small"
        @click="startEdit"
      >
        <v-icon>mdi-pencil-outline</v-icon>
        <v-tooltip activator="parent" location="bottom">Edit account &amp; license</v-tooltip>
      </v-btn>
    </div>

    <!-- View mode -->
    <div v-if="!editing">
      <div class="info-row">
        <span class="info-label">Type</span>
        <span class="info-value">
          <v-chip v-if="org?.orgType" size="x-small" :color="typeColor(org.orgType)" variant="tonal">
            {{ org.orgType }}
          </v-chip>
          <span v-else class="text-disabled">—</span>
        </span>
      </div>
      <div class="info-row">
        <span class="info-label">Status</span>
        <span class="info-value">{{ org?.accountStatus || '—' }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Point of contact</span>
        <span class="info-value">
          <template v-if="org?.pocName || org?.pocEmail">
            {{ org?.pocName || '—' }}<template v-if="org?.pocEmail"> · {{ org.pocEmail }}</template>
          </template>
          <span v-else class="text-disabled">—</span>
        </span>
      </div>
      <div class="info-row">
        <span class="info-label">License period</span>
        <span class="info-value">
          <template v-if="org?.licenseStartDate || org?.licenseEndDate">
            {{ org?.licenseStartDate || '—' }} → {{ org?.licenseEndDate || '—' }}
          </template>
          <span v-else class="text-disabled">—</span>
        </span>
      </div>
      <div class="info-row">
        <span class="info-label">Seats</span>
        <span class="info-value">
          {{ seatsUsed }} / {{ org?.licensedSeats ?? '—' }}
          <v-chip v-if="seatsOver" size="x-small" color="error" variant="tonal" class="ml-2">Over limit</v-chip>
        </span>
      </div>
      <div v-if="org?.accountNotes" class="info-row align-start">
        <span class="info-label">Notes</span>
        <span class="info-value text-pre-wrap">{{ org.accountNotes }}</span>
      </div>
    </div>

    <!-- Edit mode -->
    <div v-else>
      <v-select
        v-model="form.orgType"
        :items="ORG_TYPES"
        label="Type"
        variant="outlined"
        density="compact"
        clearable
        class="mb-2"
      />
      <v-select
        v-model="form.accountStatus"
        :items="STATUSES"
        label="Status"
        variant="outlined"
        density="compact"
        clearable
        class="mb-2"
      />
      <v-text-field
        v-model="form.pocName"
        label="POC name"
        variant="outlined"
        density="compact"
        class="mb-2"
      />
      <v-text-field
        v-model="form.pocEmail"
        label="POC email"
        type="email"
        variant="outlined"
        density="compact"
        :rules="[rules.email]"
        class="mb-2"
      />
      <div class="d-flex" style="gap: 12px">
        <v-text-field
          v-model="form.licenseStartDate"
          label="License start"
          type="date"
          variant="outlined"
          density="compact"
          class="mb-2"
        />
        <v-text-field
          v-model="form.licenseEndDate"
          label="License end"
          type="date"
          variant="outlined"
          density="compact"
          class="mb-2"
        />
      </div>
      <v-text-field
        v-model="form.licensedSeats"
        label="Licensed seats"
        type="number"
        min="0"
        variant="outlined"
        density="compact"
        :hint="`${seatsUsed} currently in use`"
        persistent-hint
        class="mb-2"
      />
      <v-textarea
        v-model="form.accountNotes"
        label="Account notes"
        variant="outlined"
        density="compact"
        rows="3"
        auto-grow
        class="mb-2 mt-2"
      />
      <div class="d-flex" style="gap: 8px">
        <v-btn color="primary" variant="flat" :loading="saving" @click="save">Save</v-btn>
        <v-btn variant="text" :disabled="saving" @click="cancel">Cancel</v-btn>
      </div>
    </div>
  </v-card>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { useStore } from 'vuex'

const props = defineProps({
  orgId: {
    type: String,
    required: true
  }
})

const store = useStore()

const ORG_TYPES = ['Client', 'Internal', 'POC']
const STATUSES = ['Trial', 'Active', 'Expired', 'Suspended']

const rules = {
  email: (v) => !v || /.+@.+\..+/.test(v) || 'Invalid email'
}

const org = computed(() => store.state.organisations.organisations?.find((o) => o.id === props.orgId))

const seatsUsed = computed(() => store.getters['orgMembers/getMembersByOrgId'](props.orgId).length)
const seatsOver = computed(() =>
  org.value?.licensedSeats != null && seatsUsed.value > org.value.licensedSeats
)

const typeColor = (type) => {
  if (type === 'Client') {
    return 'success'
  }
  if (type === 'POC') {
    return 'warning'
  }

  return 'grey'
}

const editing = ref(false)
const saving = ref(false)
const form = reactive({
  orgType: null,
  accountStatus: null,
  pocName: '',
  pocEmail: '',
  licenseStartDate: '',
  licenseEndDate: '',
  licensedSeats: '',
  accountNotes: ''
})

const startEdit = () => {
  const o = org.value || {}
  form.orgType = o.orgType || null
  form.accountStatus = o.accountStatus || null
  form.pocName = o.pocName || ''
  form.pocEmail = o.pocEmail || ''
  form.licenseStartDate = o.licenseStartDate || ''
  form.licenseEndDate = o.licenseEndDate || ''
  form.licensedSeats = o.licensedSeats ?? ''
  form.accountNotes = o.accountNotes || ''
  editing.value = true
}

const cancel = () => {
  editing.value = false
}

const save = async () => {
  saving.value = true
  const result = await store.dispatch('organisations/adminUpdateOrganisationAccount', {
    id: props.orgId,
    orgType: form.orgType || null,
    accountStatus: form.accountStatus || null,
    pocName: form.pocName || null,
    pocEmail: form.pocEmail || null,
    licenseStartDate: form.licenseStartDate || null,
    licenseEndDate: form.licenseEndDate || null,
    licensedSeats: form.licensedSeats === '' || form.licensedSeats === null ? null : Number(form.licensedSeats),
    accountNotes: form.accountNotes || null
  })
  saving.value = false
  if (result) {
    editing.value = false
  }
}
</script>

<style scoped>
.info-row {
  display: flex;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
.info-row:last-child {
  border-bottom: none;
}
.info-label {
  flex: 0 0 130px;
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.6);
}
.info-value {
  font-size: 14px;
}
.text-pre-wrap {
  white-space: pre-wrap;
}
</style>
